import { useAuth } from '@clerk/nextjs'
import {
  ArrowRightIcon,
  CheckIcon,
  Cross1Icon,
  LightningBoltIcon,
  MagnifyingGlassIcon
} from '@radix-ui/react-icons'
import Link from 'next/link'
import { InlineWidget } from 'react-calendly'

import { websitePaths } from 'domains/website/website.paths'
import { getDocsQuickStartPath } from 'lib/routes/replace-dynamic-paths'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import { IconStyled } from 'components/atomic/atoms/icon'
import { GitBranch } from 'components/atomic/atoms/icons/git-branch'
import { Shield } from 'components/atomic/atoms/icons/shield'
import Text from 'components/atomic/atoms/text'
import Modal from 'components/atomic/molecules/modal'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import SharedCta from 'components/pages/@shared/cta'
import Divider from 'components/pages/@shared/divider'
import { ChartIcon } from 'components/pages/features/edge-caching/icons/chart-icon'
import { SpeedIcon } from 'components/pages/features/edge-caching/icons/speed-icon'
import HomepageSectionTitle from 'components/pages/index/@shared/homepage-section-title'

const p = 125
const container = 'xl'

const sections = [
  {
    title: 'Edge Gateway',
    text: 'Connect databases and APIs to a single GraphQL endpoint at the edge.',
    icon: SpeedIcon,
    href: websitePaths.edgeGateway
  },
  {
    title: 'Auth',
    text: 'Configure auth in minutes with your favorite identity providers.',
    icon: Shield,
    href: websitePaths.auth
  },
  {
    title: 'Edge Caching',
    text: 'Cache any data source and make it lightning fast globally.',
    icon: LightningBoltIcon,
    href: websitePaths.edgeCaching
  },
  {
    title: 'Branching',
    text: 'Collaborate on changes with instant preview branches.',
    icon: GitBranch,
    href: websitePaths.branching
  },
  {
    title: 'Search',
    text: 'Fuzzy and typo-tolerant serverless search built-in.',
    icon: MagnifyingGlassIcon,
    href: websitePaths.docs + '/search'
  },
  {
    title: 'Analytics',
    text: 'Monitor performance and identify bottlenecks in realtime.',
    icon: ChartIcon,
    href: websitePaths.analytics
  }
]

const tableData = [
  {
    name: 'No Vendor Lock-In',
    description:
      'Build and deploy GraphQL APIs without learning a new config language',
    grafbase: true,
    hasura: false
  },
  {
    name: 'Zero Config Local Development',
    description:
      'Run the entire Grafbase platform locally using a single command',
    grafbase: true,
    hasura: false
  },
  {
    name: 'Instant Preview Branches',
    description: 'Every branch and commit gets its own GraphQL API',
    grafbase: true,
    hasura: false
  },
  {
    name: 'TypeScript Configuration',
    description: 'No error-prone Yaml files to work with',
    grafbase: true,
    hasura: false
  },
  {
    name: 'Simple Schema Transformations',
    description:
      'Transform connected schemas with fully type-safety without the fuss',
    grafbase: true,
    hasura: false
  },
  {
    name: 'Edge Resolvers',
    description:
      'Extend connected APIs with your own code deployed to the edge',
    grafbase: true,
    appSync: false
  },
  {
    name: 'Edge Caching',
    description: 'Cache everything or just what you want, at no extra cost',
    grafbase: true,
    appSync: false
  },
  {
    name: 'Search Built-In',
    description:
      'Index data from anywhere using the built-in serverless search index',
    grafbase: true,
    appSync: false
  }
]

const AppSyncPage = () => {
  const { isSignedIn } = useAuth()

  return (
    <LayoutWebsite
      meta={{
        title: 'AppSync Alternative',
        imageUrl: '/images/og/app-sync.png'
      }}
      paddingTop={false}
      darkMode
    >
      <Box
        as="section"
        relative
        css={{
          overflow: 'hidden'
        }}
      >
        <Box
          relative
          css={{
            zIndex: 1
          }}
        >
          <Box
            flex="column"
            justify="center"
            items="center"
            css={{
              minHeight: '100vh'
            }}
          >
            <Box flex justify="center" items="center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 400 201"
                width={400}
                height={201}
                fill="none"
              >
                <circle
                  cx={200}
                  cy={199.5}
                  r={199}
                  fill="url(#a)"
                  stroke="#236E4A"
                  opacity={0.1}
                />
                <circle
                  cx={200}
                  cy={199.5}
                  r={146}
                  fill="url(#b)"
                  stroke="#236E4A"
                  opacity={0.2}
                />
                <circle
                  cx={200}
                  cy={199.5}
                  r={100}
                  fill="url(#c)"
                  stroke="#236E4A"
                  opacity={0.4}
                />
                <circle
                  cx={199.5}
                  cy={199.5}
                  r={57}
                  fill="url(#d)"
                  stroke="#236E4A"
                  opacity={0.7}
                />
                <circle
                  cx={200}
                  cy={199}
                  r={19.5}
                  fill="url(#e)"
                  stroke="#236E4A"
                  opacity={0.7}
                />
                <defs>
                  <linearGradient
                    id="a"
                    x1={200}
                    x2={200}
                    y1={0}
                    y2={199.768}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#418C68" />
                    <stop offset={1} stopColor="#161618" />
                  </linearGradient>
                  <linearGradient
                    id="b"
                    x1={200}
                    x2={200}
                    y1={53}
                    y2={199.697}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#418C68" />
                    <stop offset={1} stopColor="#161618" />
                  </linearGradient>
                  <linearGradient
                    id="c"
                    x1={200}
                    x2={200}
                    y1={99}
                    y2={199.635}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#418C68" />
                    <stop offset={1} stopColor="#161618" />
                  </linearGradient>
                  <linearGradient
                    id="d"
                    x1={199.5}
                    x2={199.5}
                    y1={142}
                    y2={199.577}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#418C68" />
                    <stop offset={1} stopColor="#161618" />
                  </linearGradient>
                  <linearGradient
                    id="e"
                    x1={200}
                    x2={200}
                    y1={179}
                    y2={199.027}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#418C68" />
                    <stop offset={1} stopColor="#161618" />
                  </linearGradient>
                </defs>
              </svg>
            </Box>

            <Text
              as="h1"
              weight="bold"
              css={{
                mt: '$6',
                fontSize: 50,
                lineHeight: 0.9,
                letterSpacing: '$tighter',
                '@lg': {
                  fontSize: 78,
                  lineHeight: 1
                }
              }}
              align="center"
            >
              AppSync alternative
            </Text>
            <Text
              as="p"
              size="lg"
              color="text3"
              align="center"
              css={{ container: 'md', mt: '$4' }}
            >
              Grafbase enables developers to ship data APIs faster with modern
              tooling.
            </Text>
            <Box
              flex
              items="center"
              justify="center"
              css={{
                mt: '$8',
                gap: '$12'
              }}
            >
              <Box
                as={Link}
                href={
                  isSignedIn ? getDocsQuickStartPath() : websitePaths.register
                }
              >
                <Button variant="gradient" size="lg">
                  Get Started
                </Button>
              </Box>
              <Modal.Root>
                <Modal.Trigger>
                  <Text underlineHover color="text3">
                    Schedule Demo{' '}
                    <IconStyled
                      as={ArrowRightIcon}
                      css={{ display: 'inline' }}
                    />
                  </Text>
                </Modal.Trigger>
                <Modal.Content
                  size="lg"
                  css={{
                    height: '100%',
                    '@sm': { height: 'unset' },
                    bgc: 'rgb(243 244 246)',
                    '.dark &': { bgc: 'rgb(16 16 16)' },
                    [`> ${Box}`]: {
                      height: '90vh',
                      '.calendly-inline-widget': {
                        height: 'inherit !important'
                      },
                      '@md': {
                        height: 450,
                        position: 'relative',
                        overflow: 'hidden',
                        '.calendly-inline-widget': {
                          height: '700px !important',
                          position: 'absolute',
                          top: -70,
                          left: 0,
                          right: 0
                        }
                      }
                    }
                  }}
                >
                  <InlineWidget url="https://cal.com/team/grafbase/demo" />
                </Modal.Content>
              </Modal.Root>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box style={{ transform: 'rotate(180deg' }}>
        <Divider
          lineBg="linear-gradient(to right, rgb(56, 227, 166, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          lineBgDark="linear-gradient(to right, rgb(126, 130, 128, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          bg="linear-gradient(250deg, rgb(56, 227, 166, 0.5) 0%, rgb(20, 23, 22, 0.5) 100%)"
          bgDark="linear-gradient(250deg, rgb(47, 50, 49, 1) 0%, rgb(20, 23, 22, 0.5) 100%)"
        />
      </Box>
      <Box
        as="section"
        css={{
          py: p
        }}
      >
        <Box css={{ container }}>
          <HomepageSectionTitle title="Batteries included" />
          <Box flex justify="center" css={{ mt: '$4' }}>
            <Text
              as="p"
              size="lg"
              color="text2"
              align="center"
              css={{ maxWidth: '$3xl' }}
            >
              Grafbase comes with everything you need to build powerful GraphQL
              APIs.
            </Text>
          </Box>
          <Box grid css={{ '@md': { gridCols: 3 }, gap: '$12', mt: '$16' }}>
            {sections.map(({ title, text, href, ...props }) => (
              <Box
                key={title}
                relative
                css={{
                  p: '$5'
                }}
              >
                <Box flex items="center" css={{ gap: '$3' }}>
                  <Box
                    css={{
                      background:
                        'linear-gradient(135deg, #595960 -0.34%, #262629 99.66%);',
                      borderRadius: '$lg',
                      p: '$2',
                      color: '$text1'
                    }}
                  >
                    <props.icon height={25} width={25} />
                  </Box>
                  <Text as="h3" size="2xl" weight="bold">
                    {title}
                  </Text>
                </Box>
                <Text as="p" color="text3" css={{ mt: '$4' }}>
                  {text}
                </Text>
                <Link href={href}>
                  <Button variant="secondary" css={{ mt: '$4' }}>
                    Learn More
                  </Button>
                </Link>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box style={{ transform: 'rotate(180deg' }}>
        <Divider
          lineBg="linear-gradient(to right, rgb(56, 227, 166, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          lineBgDark="linear-gradient(to right, rgb(126, 130, 128, 0.1) 100%, rgb(20, 23, 22, 0.1) 100%)"
          bg="linear-gradient(250deg, rgb(56, 227, 166, 0.5) 0%, rgb(20, 23, 22, 0.5) 100%)"
          bgDark="linear-gradient(250deg, rgb(47, 50, 49, 1) 0%, rgb(20, 23, 22, 0.5) 100%)"
        />
      </Box>
      <Box
        as="section"
        css={{
          py: p
        }}
      >
        <Box css={{ container }}>
          <HomepageSectionTitle title="Build and ship APIs faster with Grafbase" />
          <Box flex justify="center" css={{ mt: '$4' }}>
            <Text
              as="p"
              size="lg"
              color="text2"
              align="center"
              css={{ maxWidth: '$3xl' }}
            >
              Stop spending time learning a new template language and focus on
              shipping products.
            </Text>
          </Box>
          <Box
            as="table"
            css={{
              borderRadius: '$lg',
              width: '$full',
              bgc: '$bg2',
              mt: '$12',
              '& th, td': {
                padding: '$3'
              },
              '& tbody tr td:not(:first-child)': {
                textAlign: 'center'
              },
              '& tbody tr:not(:last-child)': {
                borderBottom: '1px solid $bg3'
              }
            }}
          >
            <Box as="thead">
              <Box as="tr">
                <Box
                  as="th"
                  css={{ bgc: '$bg3', borderTopLeftRadius: '$lg' }}
                />
                <Box
                  as="th"
                  css={{ bgc: '$bg3', color: '$text1', width: '20%' }}
                >
                  Grafbase
                </Box>
                <Box
                  as="th"
                  css={{
                    bgc: '$bg3',
                    borderTopRightRadius: '$lg',
                    color: '$text1',
                    width: '20%'
                  }}
                >
                  AppSync
                </Box>
              </Box>
            </Box>
            <Box as="tbody">
              {tableData.map(
                ({ name, description, grafbase, appSync }, index) => (
                  <Box key={index} as="tr">
                    <Box as="td" css={{ lineHeight: '$lg' }}>
                      <Text
                        css={{
                          color: 'white',
                          fontWeight: '$medium',
                          display: 'block'
                        }}
                        as="p"
                      >
                        {name}
                      </Text>
                      {!!description && (
                        <Text
                          css={{
                            color: '$text3',
                            fontSize: '$sm',
                            display: 'none',
                            '@md': {
                              display: 'block'
                            }
                          }}
                          as="p"
                        >
                          {description}
                        </Text>
                      )}
                    </Box>
                    <Box as="td" css={{ textAlign: 'center' }}>
                      <Box flex items="center" justify="center">
                        {grafbase ? (
                          <Box
                            css={{
                              bgc: '$success',
                              p: '$0-5',
                              borderRadius: '$lg'
                            }}
                          >
                            <IconStyled
                              as={CheckIcon}
                              color="text1"
                              size="lg"
                            />
                          </Box>
                        ) : (
                          <Box
                            css={{
                              bgc: '$error',
                              p: '$0-5',
                              borderRadius: '$lg'
                            }}
                          >
                            <IconStyled
                              as={Cross1Icon}
                              color="text1"
                              size="lg"
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box as="td">
                      <Box flex items="center" justify="center">
                        {appSync ? (
                          <Box
                            css={{
                              bgc: '$success',
                              p: '$0-5',
                              borderRadius: '$lg'
                            }}
                          >
                            <IconStyled
                              as={CheckIcon}
                              color="text1"
                              size="lg"
                            />
                          </Box>
                        ) : (
                          <Box
                            css={{
                              bgc: '$error',
                              p: '$0-5',
                              borderRadius: '$lg'
                            }}
                          >
                            <IconStyled
                              as={Cross1Icon}
                              color="text1"
                              size="lg"
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box as="section">
        <SharedCta />
      </Box>
    </LayoutWebsite>
  )
}
export default AppSyncPage
